import { useMemo } from 'react'
import type { FeedbackDailyState } from '@/hooks/useFeedbackDaily'
import { buildFeedbackDailyFigure } from '@/lib/analytics/figures'
import { readChartPalette } from '@/lib/analytics/palette'
import ChartCard from '@/components/ChartCard'
import Plot from '@/components/Plot'
import styles from './FeedbackInsights.module.css'

export type FeedbackInsightsProps = {
  state: FeedbackDailyState
}

/** Feedback-per-day card. The date range is owned by the Statistikk page (so it
 * stays in sync with the usage charts); this renders the series via the same
 * Plotly surface as the other charts. */
export default function FeedbackInsights({ state }: FeedbackInsightsProps) {
  const palette = useMemo(() => readChartPalette(), [])
  const figure = useMemo(
    () => (state.kind === 'ready' ? buildFeedbackDailyFigure(state.points, palette) : null),
    [state, palette],
  )

  return (
    <ChartCard wide title="Tilbakemeldinger" description="Tommel opp mot tommel ned per dag.">
      {state.kind === 'loading' && (
        <p className={styles.status} role="status">
          Laster tilbakemeldinger…
        </p>
      )}
      {state.kind === 'error' && (
        <p className={styles.error} role="alert">
          Kunne ikke laste tilbakemeldinger: {state.message}
        </p>
      )}
      {state.kind === 'ready' && figure !== null && (
        <Plot
          data={figure.data}
          layout={figure.layout}
          ariaLabel="Søylediagram: positive mot negative tilbakemeldinger per dag"
        />
      )}
    </ChartCard>
  )
}
